import { Component, Input, OnInit } from '@angular/core';
import { StudentIssueViewModel } from 'edu-application/dist';
import { CompletionAnimationService } from '../../shared/services/completion-animation.service';
import { CompletionType } from '../../shared/models/completion-type.enum';
import { IssuesService } from '../issues.service';
import { TodoItem } from 'edu-core';

@Component({
  selector: 'edu-student-issues-card',
  templateUrl: './student-issues-card.component.html',
  styleUrls: ['./student-issues-card.component.scss']
})
export class StudentIssuesCardComponent implements OnInit {
  /**
   * The issues of the student
   */
  @Input() issues: StudentIssueViewModel[] = [];

  /**
   * The username of the student
   */
  @Input() username: string = '';

  /**
   * The name of the student
   */
  @Input() studentName: string = '';

  /**
   * Determines whether the component is in a readonly state.
   * When set to true, the component's editable features will be disabled.
   * @default false
   */
  @Input() readonly: boolean = false;

  /**
   * Sorted issues by priority
   */
  sortedIssues: StudentIssueViewModel[] = [];

  /**
   * Tracks which issues are completed
   */
  completedIssues: { [key: string]: boolean } = {};

  /**
   * Tracks which todo items are completed for each issue
   */
  todoCompletionStatus: { [issueId: string]: boolean[] } = {};

  constructor(
    private completionAnimationService: CompletionAnimationService,
    private studentIssuesService: IssuesService
  ) { }

  ngOnInit(): void {
    this.sortIssues();
    this.initializeTodoStatus();
  }

  /**
   * Sort issues by completion status and then by priority
   * Completed issues will be placed at the bottom
   */
  sortIssues(): void {
    // Define priority order (highest to lowest)
    const priorityOrder = ['Critical', 'High', 'Medium', 'Low'];

    this.sortedIssues = [...this.issues].sort((a, b) => {
      // First, sort by completion status
      const isACompleted = this.isIssueCompleted(a.id);
      const isBCompleted = this.isIssueCompleted(b.id);

      if (isACompleted && !isBCompleted) {
        return 1; // A is completed, B is not, so A goes after B
      }
      if (!isACompleted && isBCompleted) {
        return -1; // A is not completed, B is completed, so A goes before B
      }

      // If both are in the same completion state, sort by priority
      return priorityOrder.indexOf(a.priority) - priorityOrder.indexOf(b.priority);
    });
  }

  /**
   * Initialize the todo completion status based on the initial state of todoItems
   */
  initializeTodoStatus(): void {
    this.issues.forEach(issue => {
      if (issue.todoItems && issue.todoItems.length > 0) {
        this.todoCompletionStatus[issue.id] = issue.todoItems.map(item => item.completed || false);
      }
    });
  }

  /**
   * Toggle completion status of an issue
   */
  toggleCompletion(issueId: string): void {
    this.completedIssues[issueId] = !this.completedIssues[issueId];
  }

  /**
   * Check if an issue is completed (all todo items are completed)
   */
  isIssueCompleted(issueId: string): boolean {
    const issue = this.issues.find(i => i.id === issueId);
    if (!issue || !issue.todoItems || issue.todoItems.length === 0) {
      return false;
    }

    return issue.todoItems.every(item => item.completed);
  }

  /**
   * Toggle completion status of a todo item - update to resort issues
   */
  async toggleTodoCompletion(issueId: string, todoIndex: number): Promise<void> {
    if (this.readonly) return;
    // Find the issue and todo item
    const issue = this.sortedIssues.find(i => i.id === issueId);
    if (issue && issue.todoItems && issue.todoItems[todoIndex]) {
      // Toggle completion status
      const todoItem = issue.todoItems[todoIndex];
      todoItem.completed = !todoItem.completed;
      this.postToggleItemUpdate(todoItem, issueId);

      // If item was completed, show animation with the task name
      if (todoItem.completed) {
        this.completionAnimationService.showCompletionAnimation(
          todoItem.title,
          'Todo Item Completed!',
          false,
          null,
          CompletionType.TODO_ITEM
        );
      }

      // If the todo item has subitems, update all subitems to match the parent's completed status
      if (issue.todoItems[todoIndex].subItems && issue.todoItems[todoIndex].subItems.length > 0) {
        const newStatus = issue.todoItems[todoIndex].completed;
        issue.todoItems[todoIndex].subItems.forEach(subItem => {
          subItem.completed = newStatus;
        });
      }

      // Initialize the todoCompletionStatus if needed
      if (!this.todoCompletionStatus[issueId]) {
        this.todoCompletionStatus[issueId] = issue.todoItems.map(item => item.completed || false);
      } else {
        this.todoCompletionStatus[issueId][todoIndex] = issue.todoItems[todoIndex].completed;
      }

      // Check if this completion means the entire issue is now completed
      this.checkForIssueCompletion(issue);

      // Resort issues if needed (this avoids duplicate sorting since checkForIssueCompletion
      // will also sort issues if the issue becomes completed)
      if (!this.isIssueCompleted(issueId)) {
        this.sortIssues();
      }
    }
  }

  /**
   * Toggle completion status of a sub-item
   */
  toggleSubItemCompletion(issueId: string, todoIndex: number, subItemIndex: number): void {
    if (this.readonly) return;
    // Find the issue and todo item
    const issue = this.sortedIssues.find(i => i.id === issueId);
    if (issue &&
      issue.todoItems &&
      issue.todoItems[todoIndex]?.subItems &&
      issue.todoItems[todoIndex].subItems[subItemIndex]) {
      // Toggle completion status
      const subItem = issue.todoItems[todoIndex].subItems[subItemIndex];
      subItem.completed = !subItem.completed;
      this.postToggleItemUpdate(subItem, issueId);
      // If item was completed, show animation with the subtask name
      if (subItem.completed) {
        this.completionAnimationService.showCompletionAnimation(
          subItem.title,
          'Subtask Completed!',
          false,
          null,
          CompletionType.TASK // Regular task for subtasks
        );
      }

      // Update parent todo item completion status based on all sub-items
      if (issue.todoItems[todoIndex].subItems.every(s => s.completed)) {
        // If all sub-items are completed, mark the parent as completed
        if (!issue.todoItems[todoIndex].completed) {
          this.toggleTodoCompletion(issueId, todoIndex);
        }
      } else {
        // If any sub-item is not completed, ensure the parent is not marked as completed
        if (issue.todoItems[todoIndex].completed) {
          this.toggleTodoCompletion(issueId, todoIndex);
        }
      }
    }
  }

  private async postToggleItemUpdate(todoItem: TodoItem, issueId: string) {
    if (this.readonly) return;
    try {
      await this.studentIssuesService.toggleTodoItem({
        id: todoItem.id,
        studentIssueId: issueId,
        completed: todoItem.completed
      })
    } catch (e) {
      return;
    }
  }
  /**
   * Check if an issue is now fully completed and show the issue completion animation
   */
  private checkForIssueCompletion(issue: StudentIssueViewModel): void {
    if (issue.todoItems && issue.todoItems.length > 0) {
      const allTodosCompleted = issue.todoItems.every(item => item.completed);

      // If all todos are now completed and this issue wasn't previously marked as completed
      if (allTodosCompleted && !this.completedIssues[issue.id]) {
        this.completedIssues[issue.id] = true;

        // Show the issue completion animation
        this.completionAnimationService.showCompletionAnimation(
          issue.title,
          'Issue Completed!',
          true, // Show points for issue completion
          null,   // Points value
          CompletionType.ISSUE
        );

        // Re-sort issues to move completed ones to the bottom
        this.sortIssues();
      }
    }
  }

  /**
   * Get severity class based on severity level
   */
  getSeverityClass(severity: string): string {
    switch (severity.toLowerCase()) {
      case 'high': return 'severity-high';
      case 'medium': return 'severity-medium';
      case 'low': return 'severity-low';
      default: return '';
    }
  }

  /**
   * Get severity icon based on severity level
   */
  getSeverityIcon(severity: string): string {
    switch (severity.toLowerCase()) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'help';
    }
  }

  /**
   * Get priority icon based on priority level
   */
  getPriorityIcon(priority: string): string {
    switch (priority.toLowerCase()) {
      case 'critical': return 'flag';
      case 'high': return 'priority_high';
      case 'medium': return 'low_priority';
      case 'low': return 'arrow_downward';
      default: return 'help';
    }
  }

  /**
   * Get color for priority indicator
   */
  getPriorityColor(priority: string): string {
    switch (priority.toLowerCase()) {
      case 'critical': return '#e03131';
      case 'high': return '#f76707';
      case 'medium': return '#1971c2';
      case 'low': return '#2f9e44';
      default: return 'transparent';
    }
  }
}
